import { IMetricModelOutput } from "@edgetier/watchtower-types";
import { differenceInCalendarDays, compareAsc, isSameDay, startOfHour, isSameHour, differenceInDays } from "date-fns";
import { IFormattedChartData } from "./metric-values-chart.types";

const MAX_INDEX_SPAN = 20;

/**
 * Find the middle point in each day for creating an axis label.
 * @param metricModelOutputs Array of actual and predicted metric values over time.
 * @returns                  Indexes in the chart data that are the middle of each day.
 */
const findMiddleOfDayIndexes = (metricModelOutputs: IMetricModelOutput[]) => {
    const firstDay = metricModelOutputs[0].dateTime;
    const lastDay = metricModelOutputs[metricModelOutputs.length - 1].dateTime;
    const dayCount = differenceInCalendarDays(lastDay, firstDay);

    const dayBoundaries = Array(dayCount + 1)
        .fill(0)
        .map(() => ({ firstDayIndex: 0, lastDayIndex: 0 }));

    metricModelOutputs.forEach(({ dateTime }, index, values) => {
        const dayCountIndex = differenceInCalendarDays(dateTime, firstDay);

        // The first value or any value with a different date than the previous must be the first value in that day.
        if (index === 0 || !isSameDay(dateTime, values[index - 1].dateTime)) {
            dayBoundaries[dayCountIndex].firstDayIndex = index;
        }

        // The last value or any value with a different date than the next must be the last value in that day.
        if (index === values.length - 1 || !isSameDay(dateTime, values[index + 1].dateTime)) {
            dayBoundaries[dayCountIndex].lastDayIndex = index;
        }
    });

    return dayBoundaries.map(({ firstDayIndex, lastDayIndex }) => {
        // If the indexes are too close together, the label will ovelap with the day marker so don't show these labels.
        // If the total number of indexes is large, the difference between the first and last index for each day will
        // be larger and a smaller total will make a smaller difference. Standardise this measure by dividing the total
        // number of indexes by the difference in indexes for this day.
        if (metricModelOutputs.length / (lastDayIndex - firstDayIndex) > MAX_INDEX_SPAN) return -1;
        return Math.floor((lastDayIndex + firstDayIndex) / 2);
    });
};

/**
 * Sort the chart data and find the middle and end of each day for creating an axis.
 * @param metricModelOutputs Array of actual and predicted metric values over time.
 * @returns                  Original array with the middle and last of each day highlighted for making axis labels.
 */
export const formatChartData = (metricModelOutputs: IMetricModelOutput[]): IFormattedChartData[] => {
    if (metricModelOutputs.length === 0) {
        return [];
    }

    const firstDay = metricModelOutputs[0].dateTime;
    const middleIndexes = findMiddleOfDayIndexes(metricModelOutputs);

    return metricModelOutputs
        .slice()
        .sort((one, two) => compareAsc(one.dateTime, two.dateTime))
        .map(({ actualValue, dateTime, predictedValue, predictedLower, predictedUpper }, index, values) => ({
            actualValue,
            confidenceRange: [predictedLower, predictedUpper],
            dateTime,
            time: dateTime.getTime(),
            isMiddleOfDay: middleIndexes[differenceInCalendarDays(dateTime, firstDay)] === index,
            isLastOfDay: index < values.length - 1 && !isSameDay(dateTime, values[index + 1].dateTime),
            predictedValue,
        }));
};

/**
 * Construct the x axis ticks from the chart data and derive the interval from this.
 * @param chartData The data that will be displayed in the chart.
 * @returns An array of dates in a number format that is used to label the x axis of the chart and the tick interval.
 */
export const getXAxisData = (chartData: IFormattedChartData[]): [number[], number] => {
    const ticks = chartData
        .map(({ time }) => time)
        .reduce<number[]>((roundedTimes, time) => {
            // Check if the previous time is in the same hour as the current one. We don't want to add both because it
            // will result in two ticks being the same right next to each other once they're formatted e.g 11 11.
            if (roundedTimes.length > 0 && isSameHour(roundedTimes[roundedTimes.length - 1], time)) {
                return roundedTimes;
            }
            // Round the time to the nearest hour so that the label is just at the start of every hour.
            return [...roundedTimes, startOfHour(time).getTime()];
        }, []);

    // Increase the interval when there is a large difference in days because otherwise there will be too many labels
    // on the x axis and it will be hard to read.
    const startAndEndDifference = differenceInDays(ticks[ticks.length - 1], ticks[0]);
    const tickInterval = startAndEndDifference > 0 ? startAndEndDifference - 1 : 0;

    return [ticks, tickInterval];
};
